/**
 * The builder's surface system (SB-4, D-22).
 *
 * TWO FAMILIES, and the family change is what creates depth — not a third
 * border. `DESIGN.md`'s Anti-patterns entry is the authority and carries the
 * same values; this module exists so the builder reads them from one place
 * rather than repeating literals across five components.
 *
 *   RAISED — a cream tint lifted off the canvas, for things you ACT ON.
 *   RECESSED — a dark inset, for things you LOOK INTO.
 *
 * The load-bearing consequence, and the reason this is a system rather than
 * three stacked boxes: **the muscle-summary panel is DARKER than the block
 * that contains it.** Raised block → recessed panel; raised block → raised
 * device row → recessed value pill. Two adjacent levels in the SAME family
 * share a fill and are told apart by their EDGE — a cream hairline on a device
 * row, an amber one on the block that is open.
 *
 * `<Panel>` is deliberately not used for any of these. It knows one family and
 * forbids nesting, so it could never express "the panel inside the block is
 * darker than the block". That guard is a property of `Panel`, not a law.
 *
 * Every literal below is banned by `no-restricted-syntax` in
 * `frontend/eslint.config.js` — the two cream triplets and the amber one. The
 * house answer is a disable carrying its reason, in ONE module rather than at
 * each call site; `components/catalog/surfaces.ts` is the same pattern and the
 * same reasoning. `rgba(26,20,19,…)` is not a banned triplet and is kept here
 * anyway, because splitting the system across two files by lint status would
 * make it unreadable.
 */

/* eslint-disable no-restricted-syntax -- the surface system from DESIGN.md's
   Anti-patterns entry, which carries these exact values; see this module's
   docstring and CLAUDE.md's Component Patterns section. */

/** Things you act on. Cream tint, lifted off the canvas. */
export const RAISED = {
  /** A block at rest. */
  block: 'rgba(245, 237, 225, 0.04)',
  /** A block being edited, and a device row — both interactive. */
  active: 'rgba(245, 237, 225, 0.05)',
} as const

/** Things you look into. Dark inset. */
export const RECESSED = {
  /** A readout panel: the muscle summary. Darker than the block holding it. */
  panel: 'rgba(26, 20, 19, 0.35)',
  /** A numeric field. */
  field: 'rgba(26, 20, 19, 0.40)',
  /** A persistent rail: the equipment tray. */
  rail: 'rgba(26, 20, 19, 0.45)',
} as const

/** Edges. Two adjacent levels in one family are told apart by these, never by
 *  a drop-shadow stack. */
export const EDGE = {
  /** A device row, and any resting raised surface. */
  hairline: 'rgba(245, 237, 225, 0.12)',
  /** The block that is OPEN. This is how an edited block is marked. */
  open: 'rgba(232, 197, 160, 0.22)',
} as const

/* eslint-enable no-restricted-syntax */

/** Which family a surface belongs to. Rendered as `data-surface` so a test can
 *  assert the FAMILY relationship — "the panel is recessed inside a raised
 *  block" — rather than a pixel value that any restyle would break. */
export type SurfaceFamily = 'raised' | 'recessed'
